import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { palette, color, radius, fontFamily } from "../../Shared/tokens";

const CYCLE_SECONDS = 4.8;
const CYCLE = `${CYCLE_SECONDS}s`;

// ──────────────────────────────────────────────────────────────────────────────
// FlowBoxIllustration — animated device: the two outer buttons are pressed and
// held, a progress bar fills, then all buttons flash in a left-to-right wave.
// ──────────────────────────────────────────────────────────────────────────────
export function FlowBoxIllustration({ buttonCount = 5, width = 220 }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const bodyX = 10;
  const bodyW = 200;
  const step = (bodyW - 48) / Math.max(buttonCount - 1, 1);
  const buttons = Array.from({ length: buttonCount }, (_, i) => bodyX + 24 + i * step);
  const lastIdx = buttonCount - 1;
  // Spread the wave over 10% of the cycle regardless of button count
  const waveStagger = (CYCLE_SECONDS * 0.1) / Math.max(lastIdx, 1);

  return (
    <svg
      viewBox="0 0 220 104"
      width={width}
      height={(width * 104) / 220}
      role="img"
      aria-label={t("flowBox.pairing.illustrationLabel")}
      className={classes.svg}
    >
      {/* Device body */}
      <rect x={bodyX} y={22} width={bodyW} height={58} rx={14} fill={palette.white} stroke={palette.gray[300]} strokeWidth={1.5} />

      {/* Buttons */}
      {buttons.map((cx, i) => {
        const isOuter = i === 0 || i === lastIdx;
        return (
          <circle
            key={i}
            cx={cx}
            cy={51}
            r={11}
            strokeWidth={1.5}
            className={isOuter ? classes.outerButton : classes.innerButton}
          />
        );
      })}

      {/* Connection mode: buttons flash in a wave from left to right */}
      {buttons.map((cx, i) => (
        <g key={i} className={classes.wave} style={{ animationDelay: `${i * waveStagger}s` }}>
          <circle cx={cx} cy={51} r={16} fill={palette.brand.blue} fillOpacity={0.2} />
          <circle cx={cx} cy={51} r={11} fill={palette.brand.blue} />
        </g>
      ))}

      {/* Press indicators above the outer buttons */}
      {[buttons[0], buttons[lastIdx]].map((cx, i) => (
        <path
          key={i}
          d={`M ${cx} 2 L ${cx} 14 M ${cx - 5} 9 L ${cx} 14 L ${cx + 5} 9`}
          fill="none"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={classes.arrow}
        />
      ))}

      {/* Hold progress */}
      <rect x={60} y={92} width={100} height={4} rx={2} fill={palette.gray[200]} />
      <rect x={60} y={92} width={100} height={4} rx={2} className={classes.progress} />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// FlowBoxPairingSteps — numbered steps for putting the device in connection mode
// ──────────────────────────────────────────────────────────────────────────────
export function FlowBoxPairingSteps({ className }) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <ol className={`${classes.steps} ${className || ""}`}>
      <li>{t("flowBox.pairing.step1")}</li>
      <li>{t("flowBox.pairing.step2")}</li>
      <li>{t("flowBox.pairing.step3")}</li>
    </ol>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// FlowBoxPairingGuide — illustration + numbered steps explaining how to put the
// device into connection mode.
// ──────────────────────────────────────────────────────────────────────────────
export default function FlowBoxPairingGuide({ buttonCount = 5 }) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <div className={classes.guide}>
      <div className={classes.guideTitle}>{t("flowBox.pairing.title")}</div>
      <div className={classes.guideContent}>
        <div className={classes.illustrationWrap}>
          <FlowBoxIllustration buttonCount={buttonCount} width={180} />
        </div>
        <FlowBoxPairingSteps />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────────
// Base styles describe the "held" end state, so with reduced motion the
// illustration still shows which buttons to press.
const useStyles = makeStyles(() => ({
  svg: {
    display: "block",
    flexShrink: 0,
    overflow: "visible",
  },
  innerButton: {
    fill: palette.white,
    stroke: palette.gray[400],
  },
  outerButton: {
    fill: palette.brand.blue,
    stroke: palette.brand.blue,
    transformBox: "fill-box",
    transformOrigin: "center",
    animation: `$press ${CYCLE} ease-in-out infinite`,
  },
  arrow: {
    stroke: palette.brand.blue,
    animation: `$arrow ${CYCLE} ease-in-out infinite`,
  },
  progress: {
    fill: palette.brand.blue,
    transformBox: "fill-box",
    transformOrigin: "left center",
    animation: `$progress ${CYCLE} linear infinite`,
  },
  wave: {
    opacity: 0,
    animation: `$wave ${CYCLE} ease-in-out infinite`,
  },
  "@keyframes press": {
    "0%, 8%":    { fill: palette.white, stroke: palette.gray[400], transform: "scale(1)" },
    "12%, 64%":  { fill: palette.brand.blue, stroke: palette.brand.blue, transform: "scale(0.88)" },
    "70%, 100%": { fill: palette.white, stroke: palette.gray[400], transform: "scale(1)" },
  },
  "@keyframes arrow": {
    "0%":        { opacity: 0, transform: "translateY(-6px)" },
    "8%":        { opacity: 1, transform: "translateY(0)" },
    "12%, 64%":  { opacity: 1, transform: "translateY(5px)" },
    "70%, 100%": { opacity: 0, transform: "translateY(-6px)" },
  },
  "@keyframes progress": {
    "0%, 12%":  { transform: "scaleX(0)", opacity: 1 },
    "64%, 92%": { transform: "scaleX(1)", opacity: 1 },
    "100%":     { transform: "scaleX(1)", opacity: 0 },
  },
  // Two passes; each button is offset by its animationDelay (up to 10% of the
  // cycle), so the last button's second pass ends right at 100%.
  "@keyframes wave": {
    "0%, 72%":   { opacity: 0 },
    "74.5%":     { opacity: 1 },
    "78%, 84%":  { opacity: 0 },
    "86.5%":     { opacity: 1 },
    "90%, 100%": { opacity: 0 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    outerButton: { animation: "none" },
    arrow: { animation: "none", transform: "translateY(5px)" },
    progress: { animation: "none" },
    wave: { animation: "none" },
  },
  guide: {
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    background: color.surface.subtle,
    padding: "12px 14px",
    fontFamily: fontFamily.base,
  },
  guideTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: color.text.primary,
    marginBottom: 10,
  },
  guideContent: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  illustrationWrap: {
    flexShrink: 0,
    margin: "0 auto",
  },
  steps: {
    flex: "1 1 200px",
    margin: 0,
    paddingLeft: 18,
    fontSize: 13,
    lineHeight: 1.5,
    color: color.text.secondary,
    "& li + li": { marginTop: 4 },
  },
}));
